import React from 'react';
import { Target, MapPin, Eye, Image, CheckCircle, AlertCircle } from 'lucide-react';

export default function ConfidenceBreakdownCard({ enhancedConfidence }) {
    if (!enhancedConfidence) return null;

    const { overallConfidence, breakdown, uncertaintyRadius, method } = enhancedConfidence;

    const factors = [
        {
            key: 'multiImageAgreement',
            icon: Image,
            label: 'Multi-Image Agreement',
            color: 'purple',
            description: 'Consistency across multiple photos'
        },
        {
            key: 'portugalBounds',
            icon: MapPin,
            label: 'Portugal Validation',
            color: 'green',
            description: 'Geographic bounds check'
        },
        {
            key: 'imageQuality',
            icon: Eye,
            label: 'Image Quality',
            color: 'blue',
            description: 'Resolution and clarity'
        },
        {
            key: 'architecturalMatch',
            icon: CheckCircle,
            label: 'Pattern Match',
            color: 'orange',
            description: 'Expected visual features'
        }
    ];

    const getConfidenceColor = (score) => {
        if (score >= 0.8) return 'text-green-600';
        if (score >= 0.6) return 'text-yellow-600';
        return 'text-red-600';
    };

    const getConfidenceLabel = (score) => {
        if (score >= 0.9) return 'Excellent';
        if (score >= 0.75) return 'Good';
        if (score >= 0.6) return 'Moderate';
        if (score >= 0.4) return 'Fair';
        return 'Low';
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Target className="w-5 h-5 text-emerald-600" />
                    GeoCLIP Confidence Analysis
                </h3>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">
                    Phase 1 Enhanced
                </span>
            </div>

            {/* Overall Confidence */}
            <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <span className="text-sm font-medium text-gray-700">Overall Confidence</span>
                        <p className="text-xs text-gray-500 mt-1">
                            {getConfidenceLabel(overallConfidence)} • ±{uncertaintyRadius}m precision
                        </p>
                    </div>
                    <span className={`text-3xl font-bold ${getConfidenceColor(overallConfidence)}`}>
                        {(overallConfidence * 100).toFixed(0)}%
                    </span>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-white rounded-full h-4 shadow-inner">
                    <div
                        className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                        style={{ width: `${Math.max(overallConfidence * 100, 5)}%` }}
                    >
                        {overallConfidence >= 0.15 && (
                            <span className="text-white text-xs font-bold">
                                {(overallConfidence * 100).toFixed(0)}%
                            </span>
                        )}
                    </div>
                </div>

                {/* Method */}
                <div className="mt-3 flex items-center gap-2 text-xs text-emerald-700">
                    <AlertCircle className="w-3 h-3" />
                    <span>Method: <span className="font-semibold capitalize">{method.replace(/_/g, ' ')}</span></span>
                </div>
            </div>

            {/* Confidence Factors */}
            <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Confidence Breakdown</h4>

                {factors.map(factor => {
                    const data = breakdown[factor.key];
                    if (!data) return null;

                    const Icon = factor.icon;
                    const score = data.score * 100;

                    // Dynamic color classes based on factor color
                    const colorClasses = {
                        purple: 'border-emerald-200 bg-emerald-50',
                        green: 'border-green-200 bg-green-50',
                        blue: 'border-blue-200 bg-blue-50',
                        orange: 'border-orange-200 bg-orange-50'
                    };

                    const barColors = {
                        purple: 'bg-emerald-500',
                        green: 'bg-green-500',
                        blue: 'bg-blue-500',
                        orange: 'bg-orange-500'
                    };

                    const iconColors = {
                        purple: 'text-emerald-600',
                        green: 'text-green-600',
                        blue: 'text-blue-600',
                        orange: 'text-orange-600'
                    };

                    return (
                        <div
                            key={factor.key}
                            className={`border-l-4 pl-4 py-3 rounded-r-lg ${colorClasses[factor.color]}`}
                        >
                            {/* Factor Header */}
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Icon className={`w-4 h-4 ${iconColors[factor.color]}`} />
                                    <div>
                                        <span className="text-sm font-medium text-gray-900">{factor.label}</span>
                                        <p className="text-xs text-gray-500">{factor.description}</p>
                                    </div>
                                </div>
                                <span className={`text-sm font-bold ${getConfidenceColor(data.score)}`}>
                                    {score.toFixed(0)}%
                                </span>
                            </div>

                            {/* Progress Bar */}
                            <div className="w-full bg-white rounded-full h-2 mb-2">
                                <div
                                    className={`${barColors[factor.color]} h-2 rounded-full transition-all duration-500`}
                                    style={{ width: `${score}%` }}
                                />
                            </div>

                            {/* Reason */}
                            <p className="text-xs text-gray-600 italic">"{data.reason}"</p>
                        </div>
                    );
                })}
            </div>

            {/* Footer Info */}
            <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                    <span className="font-semibold">💡 ProTip:</span> Higher confidence scores indicate more reliable geolocation.
                    Multi-image analysis with good image quality produces the best results.
                </p>
            </div>
        </div>
    );
}
